import {
  JSX, useMemo, useState,
} from 'react';
import {
  IBiomaterial, IPatient, ISubmitAssayData,
} from '@zero-dash/types';
import { GridColDef } from '@mui/x-data-grid';
import { Box } from '@mui/material';
import CustomOutlinedInput from '@/components/Common/Input';
import CustomModal from '@/components/Common/CustomModal';
import { AssaysOrderedCell } from '@/components/PatientDashboard/TimelineTab/BiomaterialsTable/AssaysOrderedCell';
import CustomDataGrid from '@/components/Common/DataGrid';
import CustomTypography from '@/components/Common/Typography';
import { chipColours } from '@/themes/colours';
import { BiomaterialIdCell } from '../BiomaterialsTable/BiomaterialIdCell';
import { IBiomaterialRow } from '@/types/PatientDashboard/TimelineView.types';

interface IConfirmationRow extends IBiomaterialRow {
  lmSubjectCode: string;
}

interface IProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (name?: string) => Promise<void>;
  dataToSubmit: ISubmitAssayData[];
  biomaterials: IBiomaterial[];
  patientRecords: IPatient[];
}

export default function SubmitConfirmationModal({
  open,
  onClose,
  onConfirm,
  dataToSubmit,
  biomaterials,
  patientRecords,
}: IProps): JSX.Element {
  const [name, setName] = useState<string>('');
  const uniqueLmSubjectCodes = useMemo(
    () => [...new Set(patientRecords.map((p) => p.lmSubjectCode.toString()))],
    [patientRecords],
  );
  const rows = useMemo<IConfirmationRow[]>(
    () => dataToSubmit.map((item) => {
      const b = biomaterials.find((bm) => bm.id === item.biomaterialId);
      const p = patientRecords.find((pr) => pr.lmSubjectId.toString() === b?.lmSubjectId);
      return {
        id: item.biomaterialId,
        name: b?.name ?? '',
        specimenPurpose: b?.specimenPurpose ?? null,
        lmSubjectCode: p?.lmSubjectCode ?? uniqueLmSubjectCodes[0] ?? '',
        volume: b?.volume ?? '',
        containerType: b?.containerType ?? '',
        storageLocation: b?.storageLocation ?? '',
        subtype: b?.subtype ?? 'DNA',
        inventoryStatus: b?.inventoryStatus ?? 'In Inventory',
        dateSentForSequencing: b?.dateSentForSequencing ?? null,
        assaysOrdered: null,
        downstreamProcess: b?.downstreamProcess ?? 'WGS_Only',
        patientId: '',
        collectionDate: { date: b?.collectionDate ?? '', daysFrom: 0 },
        panelBuilderFiles: [],
        qlimsId: '',
      };
    }),
    [dataToSubmit, biomaterials, uniqueLmSubjectCodes, patientRecords],
  );

  const confirmDisabled = rows.some(
    (row) => row.specimenPurpose === null,
  );

  const columns: GridColDef<IConfirmationRow>[] = [
    {
      field: 'name',
      headerName: 'Biomaterial Name',
      flex: 1.5,
      sortable: false,
    },
    {
      field: 'id',
      headerName: 'Biomaterial ID',
      flex: 0.8,
      sortable: false,
      renderCell: BiomaterialIdCell,
    },
    {
      field: 'specimenPurpose',
      headerName: 'Specimen Purpose',
      flex: 1.5,
      sortable: false,
    },
    {
      field: 'assays',
      headerName: 'Assays Selected',
      flex: 2,
      sortable: false,
      renderCell: ({ row }) => (
        <AssaysOrderedCell row={row} dataToSubmit={dataToSubmit} />
      ),
    },
  ];

  const handleConfirm = async (): Promise<void> => {
    onClose();
    await onConfirm(name.trim() || undefined);
  };

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      onConfirm={handleConfirm}
      confirmDisabled={confirmDisabled}
      title="Confirm submission"
      variant="create"
      buttonText={{ cancel: 'Cancel', confirm: 'Submit samples for processing' }}
      content={(
        <Box display="flex" flexDirection="column" gap={2}>
          <CustomOutlinedInput
            label="Submission name (optional)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
          />
          <CustomDataGrid
            rows={rows}
            columns={columns}
            hideFooter
            disableColumnMenu
            disableRowSelectionOnClick
            getRowHeight={() => 'auto'}
            hideCountSummary
            sx={{
              width: '100%',
              '& .MuiDataGrid-cell': {
                display: 'flex',
                alignItems: 'center',
                py: '6px',
              },
            }}
          />
          {confirmDisabled && (
            <Box
              padding="8px"
              borderRadius="8px"
              bgcolor={chipColours.chipRedBg}
              color={chipColours.chipRedText}
              border={`1px solid ${chipColours.chipRedText}`}
            >
              <CustomTypography fontWeight="medium">
                Some of the selected biomaterials do not have a specimen purpose.
                Please contact Tumour Bank to rectify.
              </CustomTypography>
            </Box>
          )}
        </Box>
      )}
    />
  );
}
